import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { partial } from 'lodash';
import { Card, Col, Row } from 'react-bootstrap';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { AppRootState, authOrgDetailsSelector, getUnreadMessageCount, MessagesActionEnum } from '../redux';
import {
  PatientFragment,
  PatientInfoQuery,
  usePatientInfoQuery,
  usePhysicianInfoQuery,
  usePhysiciansInfoQuery,
  useServiceRequestInfoQuery,
  useServiceRequestWithDocumentsInfoLazyQuery
} from '../graphql';
import { useSelector } from 'react-redux';
import { DisplayDetailComponent } from '../components/DisplayDetailComponent';
import { usePrescriptionByIdQuery } from '../graphql';
import { AnyType } from '../interfaces';
import { DBFieldSchema } from '../utils';
import { tenancyFeatureSelector } from '../redux/selectors';
import { isEmptyObject, getConfigurationDataKey } from '../utils/helpers';

interface PrescriptionDetailsInterface {
  prescriptionID: string;
  updateContent: number;
  children: React.ReactNode;
  applicationName: string;
}

export const OverviewPrescriptionDetails = (props: PrescriptionDetailsInterface) => {
  const [prescriptionFields, setPrescriptionFields]: [DBFieldSchema, AnyType] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const programId = useSelector((state: AppRootState) => state.auth.session?.user?.ProgramId);
  const { id } = useParams<{ id: string }>();
  const result = usePatientInfoQuery({ variables: { patientId: id } });
  const patient = result.data?.result;
  const organizationId = useSelector(authOrgDetailsSelector)?.organizationId;
  const { data: prescribersData } = usePhysiciansInfoQuery({
    variables: { organizationId: organizationId?.toString() || '' }
  });
  const { applicationName } = props;
  const prescribers = prescribersData?.result || [];
  const matchedPrescriber = prescribers.find(
    (prescriber) => prescriber?.id === patient?.physicians?.[0]?.physicianId.toString()
  );
  const sr = useServiceRequestInfoQuery({
    variables: {
      patientId: Number(id),
      organizationId: organizationId?.toString() || '',
      programId: programId?.toString() || ''
    }
  });
  const drugName = sr?.data?.result?.[0]?.sRDrugName ?? '';

  const setPrescriptionFieldsSchemaSchema = async () => {
    const prescriptionFieldsSchema: AnyType = await useSelector<AnyType>(
      tenancyFeatureSelector(getConfigurationDataKey('casedetails.prescription_intake'))
    );
    if (prescriptionFieldsSchema) {
      const { fields } = await prescriptionFieldsSchema;
      setPrescriptionFields(fields);
    }
  };
  setPrescriptionFieldsSchemaSchema();

  useEffect(() => {
    if (!isEmptyObject(prescriptionFields)) {
      setIsLoading(false);
    }
  }, [prescriptionFields]);

  const prescriptionDetailsResult = usePrescriptionByIdQuery({ variables: { prescriptionId: props?.prescriptionID } });
  const prescriptionDetails = prescriptionDetailsResult?.data?.prescriptionById;
  const defaultPharmacyModal = {
    prescriptionPharmacyId: null,
    prescriptionId: null,
    storeName: null,
    ncpdp: null,
    npi: null,
    addressline1: null,
    addressline2: null,
    city: null,
    state: null,
    zip: null,
    phone: null,
    fax: null,
    createdOn: null,
    createdBy: null,
    lastUpdated: null,
    lastUpdatedBy: null,
    __typename: null
  };

  const prescriptionProvider = prescriptionDetails?.prescriptionProviders?.find((p) => !p?.isSupervisingPrescriberinfo);
  const pharmacy =
    prescriptionDetails &&
    prescriptionDetails?.prescriptionPharmacy &&
    prescriptionDetails?.prescriptionPharmacy?.length > 0
      ? prescriptionDetails?.prescriptionPharmacy[0]
      : defaultPharmacyModal;
  const supervisingPrescriptionProvider = prescriptionDetails?.prescriptionProviders?.find(
    (p) => p?.isSupervisingPrescriberinfo
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getDisplayValue = (value: any) => {
    if (!value) return '---';
    return value.toString() || '---';
  };

  const getCityStateDisplayValue = (value1: any, value2: any) => {
    if (!value1 || !value2) return '---';
    return `${value1},${value2}` || '---';
  };

  const getPhoneNumberByType = (phoneNumbers: AnyType[], phoneType: string) => {
    const phone = phoneNumbers?.find((phone) => phone.phoneType === phoneType);
    return phone ? phone.phoneNumber : '---';
  };

  useEffect(() => {
    refetchPrescription(prescriptionDetailsResult, props?.prescriptionID);
  }, [props.updateContent]);

  if (isLoading) {
    return <> </>;
  }

  return (
    <>
      <Card>
        {applicationName === 'DemoTechnicaHCPPortal' && (
          <Card>
            <Card.Header className='text-secondary bg-white section__title'>
              <i className='fa-light fa-circle-arrow-up' style={{ color: '#0084A9' }}></i>
              {`Patient Intake Overview`}
            </Card.Header>
            <Card.Body className='cmx__case-detail-main-section-summary-tab__card-body'>{props.children}</Card.Body>
            <hr />
          </Card>
        )}
        <div>
          <Row>
            <Col md='12'>
              <p className='text-secondary bg-white section__title ms-3'>PATIENT INFORMATION</p>
            </Col>
          </Row>
          <Row className='ms-4'>
            <DisplayDetailComponent
              size='2'
              label={prescriptionFields?.patient_first_name?.label}
              hideField={prescriptionFields?.patient_first_name?.hidefield}
              value={getDisplayValue(patient?.firstName)}
            />
            <DisplayDetailComponent
              size='2'
              label={prescriptionFields?.patient_middle_name?.label}
              hideField={prescriptionFields?.patient_middle_name?.hidefield}
              value={getDisplayValue(patient?.middleName)}
            />
            <DisplayDetailComponent
              size='2'
              label={prescriptionFields?.patient_last_name?.label}
              hideField={prescriptionFields?.patient_last_name?.hidefield}
              value={getDisplayValue(patient?.lastName)}
            />
            <DisplayDetailComponent
              size='2'
              label={prescriptionFields?.patient_suffix?.label}
              hideField={prescriptionFields?.patient_suffix?.hidefield}
              value={getDisplayValue('')}
            />
            <DisplayDetailComponent
              size='2'
              label={prescriptionFields?.patient_date_of_birth?.label}
              hideField={prescriptionFields?.patient_date_of_birth?.hidefield}
              value={
                patient?.dob
                  ? moment.utc(getDisplayValue(patient?.dob)).format('YYYY-MM-DD')
                  : getDisplayValue(patient?.dob)
              }
            />
            <DisplayDetailComponent
              size='2'
              label={prescriptionFields?.patient_gender?.label}
              hideField={prescriptionFields?.patient_gender?.hidefield}
              value={getDisplayValue(patient?.gender)}
            />
          </Row>
          <Row className='ms-4'>
            <DisplayDetailComponent
              size='2'
              label={prescriptionFields?.patient_address_1?.label}
              hideField={prescriptionFields?.patient_address_1?.hidefield}
              value={getDisplayValue(patient?.address?.address1)}
            />
            <DisplayDetailComponent
              size='2'
              label={prescriptionFields?.patient_address_2?.label}
              hideField={prescriptionFields?.patient_address_2?.hidefield}
              value={getDisplayValue(patient?.address?.address2)}
            />
            <DisplayDetailComponent
              size='2'
              label={prescriptionFields?.patient_city?.label}
              hideField={prescriptionFields?.patient_city?.hidefield}
              value={getCityStateDisplayValue(patient?.address?.city, patient?.address?.state)}
            />
            <DisplayDetailComponent
              size='2'
              label={prescriptionFields?.patient_zip?.label}
              hideField={prescriptionFields?.patient_zip?.hidefield}
              value={getDisplayValue(patient?.address?.zip)}
            />
            <DisplayDetailComponent
              size='2'
              label={prescriptionFields?.patient_phone_1?.label}
              hideField={prescriptionFields?.patient_phone_1?.hidefield}
              value={getDisplayValue(getPhoneNumberByType([...(patient?.phoneNumbers ?? [])], 'Home'))}
            />
            <DisplayDetailComponent
              size='2'
              label={prescriptionFields?.patient_phone_2?.label}
              hideField={prescriptionFields?.patient_phone_2?.hidefield}
              value={getDisplayValue(getPhoneNumberByType([...(patient?.phoneNumbers ?? [])], 'Mobile'))}
            />
          </Row>
        </div>

        <hr />
        <div>
          <Row>
            <Col md='12'>
              <p className='section__title text-secondary ms-3'>
                <p>PRESCRIBER</p>
                <hr />
              </p>
            </Col>
          </Row>
          <div>
            <Row className='ms-4'>
              <DisplayDetailComponent
                size='2'
                label={prescriptionFields?.prescriber_first_name?.label}
                hideField={prescriptionFields?.prescriber_first_name?.hidefield}
                value={getDisplayValue(matchedPrescriber?.firstName)}
              />
              <DisplayDetailComponent
                size='2'
                label={prescriptionFields?.prescriber_middle_name?.label}
                hideField={prescriptionFields?.prescriber_middle_name?.hidefield}
                value={getDisplayValue(matchedPrescriber?.middleName)}
              />
              <DisplayDetailComponent
                size='2'
                label={prescriptionFields?.prescriber_last_name?.label}
                hideField={prescriptionFields?.prescriber_last_name?.hidefield}
                value={getDisplayValue(matchedPrescriber?.lastName)}
              />
              <DisplayDetailComponent
                size='2'
                label={prescriptionFields?.prescriber_suffix?.label}
                hideField={prescriptionFields?.prescriber_suffix?.hidefield}
                value={getDisplayValue(matchedPrescriber?.suffix)}
              />
              <DisplayDetailComponent
                size='2'
                label={prescriptionFields?.prescriber_npi?.label}
                hideField={prescriptionFields?.prescriber_npi?.hidefield}
                value={getDisplayValue(matchedPrescriber?.npi)}
              />

              {applicationName === 'DemoTechnicaHCPPortal' && (
                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.prescriber_dea?.label}
                  hideField={prescriptionFields?.prescriber_dea?.hidefield}
                  value={getDisplayValue(matchedPrescriber?.details?.dEA)}
                />
              )}

              {(applicationName === 'JanssencarepathProviderPortal' || applicationName === 'PathwaysHCP1.5') && (
                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.prescriber_email?.label}
                  hideField={prescriptionFields?.prescriber_email?.hidefield}
                  value={getDisplayValue(matchedPrescriber?.email)}
                />
              )}
            </Row>
            {applicationName === 'DemoTechnicaHCPPortal' && (
              <Row className='ms-4'>
                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.prescriber_clinic_name?.label}
                  hideField={prescriptionFields?.prescriber_clinic_name?.hidefield}
                  value={getDisplayValue('')}
                />
                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.prescriber_address_1?.label}
                  hideField={prescriptionFields?.prescriber_address_1?.hidefield}
                  value={getDisplayValue('')}
                />
                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.prescriber_address_2?.label}
                  hideField={prescriptionFields?.prescriber_address_2?.hidefield}
                  value={getDisplayValue('')}
                />
                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.prescriber_city?.label}
                  hideField={prescriptionFields?.prescriber_city?.hidefield}
                  value={getDisplayValue('')}
                />

                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.prescriber_state?.label}
                  hideField={prescriptionFields?.prescriber_state?.hidefield}
                  value={getDisplayValue('')}
                />
                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.prescriber_zip?.label}
                  hideField={prescriptionFields?.prescriber_zip?.hidefield}
                  value={getDisplayValue('')}
                />
              </Row>
            )}
            {applicationName === 'DemoTechnicaHCPPortal' && (
              <Row className='ms-4'>
                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.prescriber_phone_1?.label}
                  hideField={prescriptionFields?.prescriber_phone_1?.hidefield}
                  value={getDisplayValue('')}
                />
                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.prescriber_phone_2?.label}
                  hideField={prescriptionFields?.prescriber_phone_2?.hidefield}
                  value={getDisplayValue('')}
                />
                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.prescriber_fax?.label}
                  hideField={prescriptionFields?.prescriber_fax?.hidefield}
                  value={getDisplayValue('')}
                />
                <DisplayDetailComponent
                  size='6'
                  label={prescriptionFields?.prescriber_email?.label}
                  hideField={prescriptionFields?.prescriber_email?.hidefield}
                  value={getDisplayValue(matchedPrescriber?.email)}
                />
              </Row>
            )}
          </div>
          {applicationName === 'DemoTechnicaHCPPortal' && <hr />}
          {applicationName === 'DemoTechnicaHCPPortal' && (
            <div>
              <div className='section__title text-secondary'>
                <p className='ms-3'>DRUG/THERAPY</p>
                <hr />
              </div>
              <Row className='ms-4'>
                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.drug_drug_name?.label}
                  hideField={prescriptionFields?.drug_drug_name?.hidefield}
                  value={getDisplayValue(prescriptionDetails?.drugName)}
                />

                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.drug_dose_type?.label}
                  hideField={prescriptionFields?.drug_dose_type?.hidefield}
                  value={getDisplayValue(prescriptionDetails?.doseType)}
                />
                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.drug_quantity?.label}
                  hideField={prescriptionFields?.drug_quantity?.hidefield}
                  value={getDisplayValue(prescriptionDetails?.quantity)}
                />

                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.drug_days_supply?.label}
                  hideField={prescriptionFields?.drug_days_supply?.hidefield}
                  value={getDisplayValue(prescriptionDetails?.daysSupply)}
                />

                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.drug_date_written?.label}
                  hideField={prescriptionFields?.drug_date_written?.hidefield}
                  value={
                    prescriptionDetails?.dateWritten
                      ? moment.utc(getDisplayValue(prescriptionDetails?.dateWritten)).format('YYYY-MM-DD')
                      : getDisplayValue(prescriptionDetails?.dateWritten)
                  }
                />
              </Row>
              <Row className='ms-4'>
                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.drug_diagnoses?.label}
                  hideField={prescriptionFields?.drug_diagnoses?.hidefield}
                  value={getDisplayValue(prescriptionDetails?.diagnoses)}
                />
                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.drug_unit_of_measure?.label}
                  hideField={prescriptionFields?.drug_unit_of_measure?.hidefield}
                  value={getDisplayValue(prescriptionDetails?.unitofMeasure)}
                />

                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.drug_refills?.label}
                  hideField={prescriptionFields?.drug_refills?.hidefield}
                  value={getDisplayValue(prescriptionDetails?.refills)}
                />

                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.drug_refills_qualifier?.label}
                  hideField={prescriptionFields?.drug_refills_qualifier?.hidefield}
                  value={getDisplayValue(prescriptionDetails?.refillQualifier)}
                />

                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.drug_dispense_as_written?.label}
                  hideField={prescriptionFields?.drug_dispense_as_written?.hidefield}
                  value={getDisplayValue(prescriptionDetails?.daw)}
                />

                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.drug_last_fill_date?.label}
                  hideField={prescriptionFields?.drug_last_fill_date?.hidefield}
                  value={
                    prescriptionDetails?.lastFillDate
                      ? moment.utc(getDisplayValue(prescriptionDetails?.lastFillDate)).format('YYYY-MM-DD')
                      : getDisplayValue(prescriptionDetails?.lastFillDate)
                  }
                />

                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.drug_basis?.label}
                  hideField={prescriptionFields?.drug_basis?.hidefield}
                  value={getDisplayValue(prescriptionDetails?.drugBasis)}
                />

                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.drug_substitution_permitted?.label}
                  hideField={prescriptionFields?.drug_substitution_permitted?.hidefield}
                  value={getDisplayValue(prescriptionDetails?.substitutionPermitted)}
                />
                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.drug_ndc?.label}
                  hideField={prescriptionFields?.drug_ndc?.hidefield}
                  value={getDisplayValue(prescriptionDetails?.ndc)}
                />
                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.drug_sig?.label}
                  hideField={prescriptionFields?.drug_sig?.hidefield}
                  value={getDisplayValue(prescriptionDetails?.sig)}
                />
                <DisplayDetailComponent
                  size='2'
                  label={prescriptionFields?.drug_note?.label}
                  hideField={prescriptionFields?.drug_note?.hidefield}
                  value={getDisplayValue(prescriptionDetails?.note)}
                />
              </Row>
            </div>
          )}
        </div>
      </Card>
      {drugName && (
        <div className='cmx__case-detail-main-section-tabs__tab-content mt-3'>
          <Card>
            <Card.Body className='detail__appname text-secondary'>
              <i className='fa-light fa-circle-arrow-down' style={{ color: '#0084A9' }}></i>
              <span className='ms-2'>{drugName}</span>
            </Card.Body>
          </Card>
        </div>
      )}
    </>
  );
};

export const refetchPrescription = (caseResult: AnyType, prescriptionId: string) => {
  const queryVars = { prescriptionId: prescriptionId };
  caseResult.refetch(queryVars);
};
